export type PrdTemplateId =
  | "general-prd"
  | "web-app-saas"
  | "mobile-app"
  | "api-platform"
  | "analytics-dashboard"
  | "marketplace-listings"
  | "ecommerce-checkout"
  | "gaming-3d"
  | "crm-erp";

export type PrdTemplateIcon =
  | "file"
  | "monitor"
  | "smartphone"
  | "code"
  | "chart"
  | "store"
  | "cart"
  | "gamepad"
  | "workflow";

export type PrdTemplate = {
  id: PrdTemplateId;
  label: string;
  description: string;
  promptContext: string;
  icon: PrdTemplateIcon;
  defaults?: {
    techPreferences?: string;
    backendServices?: string;
  };
};

export const prdTemplates: PrdTemplate[] = [
  {
    id: "general-prd",
    label: "PRD (Base Template)",
    description: "Blank CodeRide PRD structure for teams that want a neutral starting point.",
    icon: "file",
    promptContext: [
      "Use this when you need a canonical PRD outline without vertical-specific prompts or defaults.",
      "Capture objectives, personas, requirements, risks, launch plan, and metrics in neutral wording.",
      "Encourage the model to ask for missing information and avoid assumptions about industry constraints.",
    ].join(" "),
  },
  {
    id: "web-app-saas",
    label: "Web App SaaS PRD",
    description: "Cloud-based web application delivered through the browser for multi-tenant customers.",
    icon: "monitor",
    promptContext: [
      "Focus on responsive UX, role-based access, subscription billing, and high availability.",
      "Call out authentication flows, tenancy boundaries, observability, and customer support surfaces.",
      "Highlight go-to-market considerations such as onboarding, paywalls, and usage analytics.",
    ].join(" "),
    defaults: {
      techPreferences: "React + TypeScript + Tailwind",
      backendServices: "Node.js API, Postgres, Stripe",
    },
  },
  {
    id: "mobile-app",
    label: "Mobile App PRD",
    description: "Native or cross-platform app targeting iOS and Android devices.",
    icon: "smartphone",
    promptContext: [
      "Include platform-specific requirements, app store compliance, and device capability considerations.",
      "Address offline mode, sync conflicts, push notifications, and mobile telemetry.",
      "Reference mobile UX patterns such as gestures, accessibility, and battery usage.",
    ].join(" "),
    defaults: {
      techPreferences: "React Native",
      backendServices: "Firebase, App Store Connect, Google Play Console",
    },
  },
  {
    id: "api-platform",
    label: "API / Developer Platform PRD",
    description: "Public or partner API surface, SDK, or developer integration platform.",
    icon: "code",
    promptContext: [
      "Emphasise endpoint design, request/response schemas, authentication scopes, and rate limiting.",
      "Capture versioning strategy, monitoring, SLAs, and developer onboarding experience.",
      "Call out documentation needs, sandbox environments, and governance for breaking changes.",
    ].join(" "),
    defaults: {
      techPreferences: "Node.js + OpenAPI",
      backendServices: "Postgres, API Gateway, OAuth 2.0",
    },
  },
  {
    id: "analytics-dashboard",
    label: "Analytics / Dashboard PRD",
    description: "Reporting or insight surface presenting metrics, charts, and drill-down analysis.",
    icon: "chart",
    promptContext: [
      "Document data sources, metric definitions, refresh cadence, and audience segmentation.",
      "Highlight permissioning, export/share features, and accessibility for visualisations.",
      "Consider data latency, alerting, benchmarking, and compliance for regulated data.",
    ].join(" "),
    defaults: {
      techPreferences: "Next.js + D3.js",
      backendServices: "Snowflake, dbt, Segment",
    },
  },
  {
    id: "marketplace-listings",
    label: "Marketplace / Listings PRD",
    description: "Two-sided marketplace or listings platform connecting suppliers and buyers.",
    icon: "store",
    promptContext: [
      "Explain supply onboarding, verification, discovery/search flows, and demand acquisition.",
      "Cover trust & safety policies, dispute resolution, reviews, and payouts/escrow mechanics.",
      "Call out regionalisation, pricing models, and analytics for both sides of the marketplace.",
    ].join(" "),
    defaults: {
      techPreferences: "Next.js + TypeScript",
      backendServices: "Supabase, Stripe Connect, Algolia",
    },
  },
  {
    id: "ecommerce-checkout",
    label: "E-Commerce / Checkout PRD",
    description: "Shopping experience including catalogue management, cart, and payment flow.",
    icon: "cart",
    promptContext: [
      "Detail product catalogue structure, promotions, inventory, and fulfilment integrations.",
      "Include checkout UX, tax/shipping rules, fraud prevention, and post-purchase lifecycle.",
      "Mention analytics, personalisation, and omnichannel touchpoints like POS or subscriptions.",
    ].join(" "),
    defaults: {
      techPreferences: "Next.js + Tailwind",
      backendServices: "Shopify API, Stripe, Shippo",
    },
  },
  {
    id: "gaming-3d",
    label: "Gaming / 3D Experience PRD",
    description: "Interactive 3D or gaming product delivered on desktop, mobile, or headset.",
    icon: "gamepad",
    promptContext: [
      "Account for core game loop, progression systems, multiplayer/latency requirements, and anti-cheat.",
      "Cover rendering performance budgets, platform compatibility, input schemes, and accessibility.",
      "Highlight monetisation strategy, live-ops tooling, and telemetry for balancing.",
    ].join(" "),
    defaults: {
      techPreferences: "Unity",
      backendServices: "Photon Realtime, PlayFab",
    },
  },
  {
    id: "crm-erp",
    label: "CRM / ERP PRD",
    description: "Internal line-of-business system for sales, operations, or enterprise resource planning.",
    icon: "workflow",
    promptContext: [
      "Focus on workflow automation, complex permissions, audit trails, and data retention policies.",
      "Document integrations with upstream/downstream systems (CRM, finance, HR) and migration strategy.",
      "Emphasise reporting, compliance, change management, and training/rollout considerations.",
    ].join(" "),
    defaults: {
      techPreferences: "React + TypeScript",
      backendServices: "Postgres, Kafka, Salesforce API",
    },
  },
];

export const defaultPrdTemplateId: PrdTemplateId = "general-prd";

export const prdTemplateMap: Record<PrdTemplateId, PrdTemplate> = prdTemplates.reduce(
  (acc, template) => {
    acc[template.id] = template;
    return acc;
  },
  {} as Record<PrdTemplateId, PrdTemplate>,
);
